import { FC, useCallback, useMemo, useState } from 'react';
import { Button, InlineNotification, TextArea } from '@carbon/react';
import { Paste } from '@carbon/react/icons';
import { format0 } from 'utils/number';
import {
  matchPastedList,
  parsePastedList,
  type PasteListOption,
} from './parse-pasted-list';
import styles from './paste-list-control.module.scss';

interface PasteListControlProps {
  id: string;
  /**
   * What is being pasted, e.g. "UWIs". Shown as an instruction to
   * "Paste [label]".
   */
  label: string;
  /**
   * The options available to select in the filter.
   */
  options: PasteListOption[];
  /**
   * Option IDs already added to the filter selection. Duplicate options added
   * via the paste list will be deduplicated.
   */
  selectedIds: string[];
  /**
   * Called with the filter's new selection: the IDs already selected plus the
   * pasted ones.
   */
  onApply: (selectedIds: string[]) => void;
  /**
   * Whether to accept a pasted value that has no matching option, sending it
   * on as an option ID.
   *
   * Set this only when `options` is non-exhaustive AND its IDs are values a
   * user could type, such as a UWI. A filter whose IDs are record IDs must
   * leave this off: the user pastes names, and an unmatched name sent as a
   * record ID matches nothing.
   */
  acceptUnmatched?: boolean;
}

/**
 * Allows a user to paste a list of values straight into a filter's selection,
 * instead of needing to select each item in the filter list manually.
 *
 * @param {PasteListControlProps} props - Describes the filter to paste into.
 * @return {JSX.Element} The Paste List toggle and editor.
 */
export const PasteListControl: FC<PasteListControlProps> = ({
  id,
  label,
  options,
  selectedIds,
  onApply,
  acceptUnmatched = false,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [result, setResult] = useState<{
    added: number;
    unmatched: string[];
  } | null>(null);

  const pastedValues = useMemo(() => parsePastedList(text), [text]);

  const handleApply = useCallback(() => {
    const { matched, unmatched } = matchPastedList(pastedValues, options);
    const toSelect = acceptUnmatched ? [...matched, ...unmatched] : matched;
    const next = [...new Set([...selectedIds, ...toSelect])];

    onApply(next);
    setResult({
      added: next.length - selectedIds.length,
      unmatched: acceptUnmatched ? [] : unmatched,
    });
    setText('');
  }, [pastedValues, options, selectedIds, onApply, acceptUnmatched]);

  if (!open) {
    return (
      <Button
        kind="ghost"
        size="sm"
        renderIcon={Paste}
        onClick={() => setOpen(true)}
      >
        Paste List
      </Button>
    );
  }

  return (
    <div className={styles.container}>
      <TextArea
        id={`paste-list-${id}`}
        labelText={`Paste ${label}`}
        helperText="One value per line, or separated by commas or tabs."
        placeholder={`Paste ${label} values`}
        value={text}
        rows={4}
        onChange={(e) => setText(e.target.value)}
      />
      <div className={styles.actions}>
        <Button
          kind="primary"
          size="sm"
          disabled={pastedValues.length === 0}
          onClick={handleApply}
        >
          Add to selection
        </Button>
        <Button
          kind="ghost"
          size="sm"
          onClick={() => {
            setOpen(false);
            setText('');
            setResult(null);
          }}
        >
          Done
        </Button>
      </div>
      {result && (
        <InlineNotification
          kind={result.unmatched.length > 0 ? 'warning' : 'success'}
          lowContrast
          hideCloseButton
          title={`${format0(result.added)} added`}
          subtitle={
            result.unmatched.length > 0
              ? `No match for: ${result.unmatched.slice(0, 10).join(', ')}${
                  result.unmatched.length > 10
                    ? ` and ${format0(result.unmatched.length - 10)} more`
                    : ''
                }`
              : undefined
          }
        />
      )}
    </div>
  );
};
